/**
 * THE PRODUCT PAGE ASKS BEFORE IT LEAVES AN APP'S BROWSER (2026-10-25).
 *
 * The owner, the night this was built: "I clicked on ad, it opened the product page but didn't show a popup of
 * trying to open in external browser? fix all!" The page used to jump by itself and say nothing; the app swallowed
 * that untapped scheme for 248 of 280 visitors. This is the question it should have asked: a bottom sheet over a
 * dimmed page, one primary button that is the tap the app WILL honour, and a plain way to stay.
 *
 * It is controlled and knows nothing about the jump: `onOpen` is the page's own `openThisInBrowser`, `onStay`
 * closes it and is also what Escape and a tap on the scrim do. `swallowed` is the page's word that the tap was
 * made and the app kept the shopper anyway — only then does the sheet show where the app's own door is
 * (the ··· menu), because a line of instructions before a tap is noise.
 *
 * Nothing here is stuck over the product (2026-10-10, the owner of the old fallback bar: "wtf is this in the
 * bottom — remove, it looks very bad"): it is modal, it covers the page only while it is being answered, and it
 * is never shown again that day (the page claims the day when it SHOWS this, not when it is answered).
 */
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useDialogA11y from '../../hooks/useDialogA11y';
import styles from './InAppBrowserPrompt.module.css';

export default function InAppBrowserPrompt({ open, os, onOpen, onStay, swallowed = false }) {
  const { t, i18n } = useTranslation();
  const sheetRef = useRef(null);
  const openRef = useRef(null);
  const isRtl = ['ar', 'he'].includes(String(i18n?.language || '').split('-')[0]);
  const android = os === 'android';

  /* Focus lands on the primary button (the sheet's one expected answer, DESIGN_SYSTEM §6.9), stays inside the sheet,
     Escape means "stay", and the page behind is inert and gets its focus back: the store's shared hook, because a
     hand-rolled dialog is a guard failure (aDialogTrapsFocusUnlessItSaysWhyNot). */
  useDialogA11y({ active: !!open, containerRef: sheetRef, onEscape: () => onStay?.(), initialFocusRef: openRef });

  if (!open || typeof document === 'undefined') return null;

  const title = android
    ? t('inApp.promptTitleAndroid', { defaultValue: 'Open in Chrome' })
    : t('inApp.promptTitleIos', { defaultValue: 'Open in Safari' });
  const why = android
    ? t('inApp.promptWhyAndroid', { defaultValue: 'Google Pay, your saved cards and a faster checkout. Your basket comes with you.' })
    : t('inApp.promptWhyIos', { defaultValue: 'Apple Pay, your saved cards and a faster checkout. Your basket comes with you.' });
  const steps = android
    ? t('inApp.promptStepsAndroid', { defaultValue: 'Tap ⋮ at the top of the screen, then "Open in browser".' })
    : t('inApp.promptStepsIos', { defaultValue: 'Tap ··· at the top of the screen, then "Open in browser".' });

  return createPortal(
    <div
      className={styles.scrim}
      dir={isRtl ? 'rtl' : 'ltr'}
      onClick={(e) => { if (e.target === e.currentTarget) onStay?.(); }}
      data-testid="inapp-prompt-scrim"
    >
      <div
        ref={sheetRef}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby="inapp-prompt-title"
        aria-describedby="inapp-prompt-why"
        data-testid="inapp-prompt"
      >
        <span className={styles.handle} aria-hidden="true" />
        <span className={styles.badge} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M15.5 8.5l-2 5-5 2 2-5z" />
          </svg>
        </span>
        <h2 id="inapp-prompt-title" className={styles.title}>{title}</h2>
        <p id="inapp-prompt-why" className={styles.why}>{why}</p>
        {swallowed ? (
          <p className={styles.steps} role="status" data-testid="inapp-prompt-steps">{steps}</p>
        ) : null}
        <button type="button" ref={openRef} className={styles.primary} onClick={() => onOpen?.()} data-testid="inapp-prompt-open">
          {title}
        </button>
        <button type="button" className={styles.secondary} onClick={() => onStay?.()} data-testid="inapp-prompt-stay">
          {t('inApp.promptStay', { defaultValue: 'Stay here' })}
        </button>
      </div>
    </div>,
    document.body,
  );
}
